import {
  Button,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import {Launch, RemoveCircleOutline} from "@material-ui/icons";
import { useCallback, useEffect, useState } from "react";
import {Loading, useNotify} from "react-admin";
import * as XLSX from "xlsx";
import {
  useQuizAssessmentAvailabilityDataProvider
} from "../../../providers/data-provider/quiz-assessment-availability.data.provider";
import { QuizAssessmentStatusResult } from "./quiz-assessment-status-result.component";
import DeleteIcon from "@material-ui/icons/Delete";
import {useQuizAssessmentTestDataProvider} from "../../../providers/data-provider/quiz-assessment-test.data.provider";
import {StatusChip, StatusPrintedChip} from "./status-chip";
import {PrintTest} from "./print-test";
import {PrintClassTest} from "./print-class-test";
import {AnswerKeyAction} from "./answer-key-action";
import {SaveResultsAction} from "./save-results-action";


const { format } = new Intl.DateTimeFormat('pt-BR', {
  month: '2-digit',
  day: '2-digit',
  year: 'numeric',
})
const dateFormatter = (date) => format(new Date(date));

const exportResults = (details) => {

  const statusMap = {
    finished: 'Concluído',
    started: 'Em andamento',
  };

  if (!details || !details.length) return;

  const isChrome = navigator.userAgent.toLowerCase().includes("chrome");

  const exportData = details.map((row) => {
    const startedAt = row.startedAt ? new Date(row.startedAt) : null;
    const finishedAt = row.finishedAt ? new Date(row.finishedAt) : null;

    if (isChrome) {
      if (startedAt) startedAt.setSeconds(startedAt.getSeconds() + 28);
      if (finishedAt) finishedAt.setSeconds(finishedAt.getSeconds() + 28);
    }

    const percentage = row.correctAnswers
      ? (row.correctAnswers * 100 / row.totalQuestions).toFixed(1) + '%'
      : '';

    return {
      'Aluno': row.studentName,
      'Turma': row.className,
      'Status': statusMap[row.status] || 'Não iniciado',
      'Início do Teste': startedAt,
      'Fim do Teste': finishedAt,
      'Total de Questões': row.totalQuestions,
      'Total de Acertos': row.correctAnswers,
      'Nota (%)': percentage
    };
  });

  const ws = XLSX.utils.json_to_sheet(exportData, {
    cellDates: true,
    dateNF: 'dd/mm/yyyy hh:mm:ss'
  });

  ws['!cols'] = [
    { wch: 25 },
    { wch: 20 },
    { wch: 18 },
    { wch: 22 },
    { wch: 22 },
    { wch: 18 },
    { wch: 18 },
    { wch: 10 }
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Resultados');

  XLSX.writeFile(wb, 'resultados_prova.xlsx');
};

export function QuizAssessmentResultsDetailsPage({ availabilityId, assessmentName, assessmentType }) {
  const [details, setDetails] = useState();
  const [loading, setLoading] = useState(false);
  const { listAvailabilityResultItem } = useQuizAssessmentAvailabilityDataProvider();
  const isPrinted = assessmentType === 'printed';

  const calcPercentage = useCallback((value, total) => {
    return (value * 100 / total).toFixed(1)
  }, [])

  const loadData = useCallback(() => {
    setLoading(true);
    listAvailabilityResultItem(availabilityId)
        .then(res => setDetails(res.data))
        .finally(() => setLoading(false));
  }, [listAvailabilityResultItem, availabilityId])

  useEffect(() => {
    loadData()
  }, [loadData])


  if (loading) {
    return <Loading />;
  }
  return (
    <Paper elevation={1}>
      <Grid container item direction="column" >
        <Grid item container spacing={1} style={{ marginBottom: '8px' }}>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Launch />}
              onClick={() => exportResults(details)}
            >
              Exportar resultados
            </Button>
          </Grid>
          {isPrinted && (
            <Grid item>
              <PrintClassTest
                availabilityId={availabilityId}
                assessmentName={assessmentName}
                variant="contained"
                color="primary"
                size="medium"
              />
            </Grid>
          )}
        </Grid>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>Aluno</TableCell>
              <TableCell>Turma</TableCell>
              <TableCell>Status</TableCell>
              {!isPrinted && <TableCell>Inicio do Teste</TableCell>}
              {!isPrinted && <TableCell>Fim do Teste</TableCell>}
              <TableCell>Total de Questões</TableCell>
              <TableCell>Total de Acertos</TableCell>
              <TableCell>Nota</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {details?.map((row) => (
              <TableRow
                hover
                key={row.testId}
              >
                <TableCell>{row.studentName}</TableCell>
                <TableCell>{row.className}</TableCell>
                <TableCell>
                  {isPrinted ? <StatusPrintedChip status={row.status} /> : <StatusChip status={row.status} />}</TableCell>
                {!isPrinted && <TableCell>{row.startedAt ? dateFormatter(row.startedAt) : ''}</TableCell>}
                {!isPrinted && <TableCell>{row.finishedAt ? dateFormatter(row.finishedAt) : ''}</TableCell>}
                <TableCell>{row.totalQuestions}</TableCell>
                <TableCell>{row.correctAnswers}</TableCell>
                <TableCell>{row.correctAnswers ? calcPercentage(row.correctAnswers, row.totalQuestions) + '%' : null}</TableCell>
                <TableCell align={'right'}>
                  {
                    !isPrinted ? (
                        [row.testId && <RemoveTestButton testId={row.testId} onRemoved={() =>  loadData()} />]
                    ) : (
                        [

                          <PrintTest
                              availabilityId={availabilityId}
                              userContextId={row.userContextId}
                              studentName={row.studentName}
                              testId={row.testId}
                              documentStatus={row.documentStatus}
                          />,
                          row.correctAnswers ?
                            <AnswerKeyAction
                              userContextId={row.userContextId}
                              testId={row.testId}
                              status={row.status}
                              onSubmitted={() => loadData()}
                              studentName={row.studentName}
                            />  :
                            <SaveResultsAction
                              studentName={row.studentName}
                              testId={row.testId}
                              status={row.status}
                              onSubmitted={() => loadData()}
                              userContextId={row.userContextId}
                            />
                        ]
                    )
                  }
                  { }
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Grid>
    </Paper>
  )
}

function RemoveTestButton({ testId, onRemoved }) {
  const { removeTest, loading } = useQuizAssessmentTestDataProvider();
  const notify = useNotify()

  const onRemoveTest = useCallback((testId)=> {
    removeTest(testId).then(() => {
      notify('Teste removido com sucesso! O aluno poderá realizar uma nova tentativa', 'success')
      onRemoved();
    }).catch(err => {
      notify('Erro ao remover teste !', 'error')
    });
  }, [notify, removeTest, onRemoved]);


  return (
      <Button variant={'contained'} disabled={loading} size={'small'} onClick={() => onRemoveTest(testId)}>
        {
          loading ? (
              <CircularProgress />
          ) : (

              "Remover Tentativa"
          )
        }
      </Button>
  )
}